import * as propz from 'propz';
import { SchoolEvent } from 'models/event';
import { VIEW_TYPE, ScreenDataModel } from '../types/tv';
import {
  getTournamentResultsFormattedByAgeGroups,
  getTournamentResultsFormattedByYearsShort,
  assignPositions,
} from '../helpers/score';
import {
  getTournamentEventAges,
  getTournamentEventGender,
} from '../helpers/event';

export function getNationalAthleticScreensData(
  events: SchoolEvent[]
): ScreenDataModel[] {
  const groupModels: ScreenDataModel[] = [];

  const eventsSorted = [...events].sort(
    (a, b) =>
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  const finishedEvents = eventsSorted.filter(
    (evt) => evt.status === 'FINISHED' && !Boolean(evt.showAsNotClosedOnPublicSite)
  );
  const hasFinishedEvents = finishedEvents.length > 0;

  const eventsToDisplay = hasFinishedEvents ? finishedEvents : eventsSorted;

  const pageSizeSingleEvent = 10;
  eventsToDisplay.forEach((evt) => {
    const isTeamEvent =
      evt.sport.players === 'TEAM' || false;

    let competitorCount = 0;
    if (isTeamEvent) {
      const allRivals: any[] = []; 
      competitorCount = 10; 
    } else {
      const individualsDataArr: any[] = propz.get(evt, ['individualsData'], []);
      competitorCount = individualsDataArr.length;
    }

    const pageCount = Math.max(1, Math.ceil(competitorCount / pageSizeSingleEvent));

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const agesLabel = getTournamentEventAges(evt.ages);
      const genderLabel = getTournamentEventGender(evt.gender);
      const officialName = propz.get(evt, ['generatedNames', 'official'], '');

      groupModels.push({
        viewType: VIEW_TYPE.SINGLE_EVENT,
        groupWithAge: `${agesLabel} ${genderLabel} ${officialName}`,
        events: [evt],
        participantsData: [{ pageIndex, pageCount }],
      });
    }
  });

  if (hasFinishedEvents) {
    const visibleEvents = events.filter(
      (evt) => !Boolean(evt.showAsNotClosedOnPublicSite)
    );

    {
      const ageGroupsData = getTournamentResultsFormattedByAgeGroups(
        visibleEvents,
        {} as any
      );
      const rawRows: any[] = propz.get(
        ageGroupsData,
        ['scores', 'Overall results'],
        []
      );
      const rankedRows = assignPositions([...rawRows]);

      const pageSize = 7;
      const pageCount = Math.max(1, Math.ceil(rankedRows.length / pageSize));

      for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
        groupModels.push({
          viewType: VIEW_TYPE.GROUP_TABLE,
          groupWithAge: 'Overall results by Age Group',
          events: visibleEvents,
          participantsData: [{ pageIndex, pageCount }],
        });
      }
    }

    {
      const byYearsData = getTournamentResultsFormattedByYearsShort(
        visibleEvents,
        {} as any
      );
      const allRows: any[] = propz.get(
        byYearsData,
        ['scores', 'Overall results'],
        []
      );

      const firstRowKeys = allRows.length > 0 ? Object.keys(allRows[0]) : [];
      const juniorCols = firstRowKeys.filter((col) => {
      const m = col.match(/^(?:Y|Year)\s*([\d\/\-]+)/);
        if (!m) return false;
        const digits = m[1].match(/\d+/g);
        if (!digits) return false;
        const nums = digits.map((d) => parseInt(d, 10));
        const mx = Math.max(...nums);
        return !isNaN(mx) && mx <= 6;
      });

      if (juniorCols.length > 0) {
        const juniorOnlyRows: any[] = allRows.map((row: any) => {
          let total = 0;
          const newRow: any = {};
          const schoolKey = firstRowKeys.find(
            (k) =>
              k !== 'Total' &&
              !/^(?:Year\s+|Y\d+)/.test(k)
          );
          if (schoolKey && row[schoolKey] !== undefined) {
            newRow[schoolKey] = row[schoolKey];
          }
          juniorCols.forEach((colKey) => {
            const rawVal = row[colKey];
            const num =
              typeof rawVal === 'number'
                ? rawVal
                : parseInt(String(rawVal), 10) || 0;
            newRow[colKey] = num;
            total += num;
          });
          newRow['Total'] = total;
          return newRow;
        });

        const rankedJuniorRows = assignPositions([...juniorOnlyRows]);
        const pageSize = 7;
        const pageCount = Math.max(1, Math.ceil(rankedJuniorRows.length / pageSize));

        for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
          groupModels.push({
            viewType: VIEW_TYPE.GROUP_TABLE,
            groupWithAge: 'Overall Junior Results',
            events: visibleEvents,
            participantsData: [{ pageIndex, pageCount }],
          });
        }
      }
    }

    {
      const byYearsData = getTournamentResultsFormattedByYearsShort(
        visibleEvents,
        {} as any
      );
      const allRows: any[] = propz.get(
        byYearsData,
        ['scores', 'Overall results'],
        []
      );
      const firstRowKeys = allRows.length > 0 ? Object.keys(allRows[0]) : [];

      const seniorCols = firstRowKeys.filter((col) => {
      const m = col.match(/^(?:Y|Year)\s*([\d\/\-]+)/);
        if (!m) return false;
        const digits = m[1].match(/\d+/g);
        if (!digits) return false;
        const nums = digits.map((d) => parseInt(d, 10));
        const mn = Math.min(...nums);
        return !isNaN(mn) && mn >= 7;
      });

      if (seniorCols.length > 0) {
        const seniorOnlyRows: any[] = allRows.map((row: any) => {
          let total = 0;
          const newRow: any = {};
          const schoolKey = firstRowKeys.find(
            (k) =>
              k !== 'Total' &&
              !/^(?:Year\s+|Y\d+)/.test(k)
          );
          if (schoolKey && row[schoolKey] !== undefined) {
            newRow[schoolKey] = row[schoolKey];
          }
          seniorCols.forEach((colKey) => {
            const rawVal = row[colKey];
            const num =
              typeof rawVal === 'number'
                ? rawVal
                : parseInt(String(rawVal), 10) || 0;
            newRow[colKey] = num;
            total += num;
          });
          newRow['Total'] = total;
          return newRow;
        });

        const rankedSeniorRows = assignPositions([...seniorOnlyRows]);
        const pageSize = 7;
        const pageCount = Math.max(1, Math.ceil(rankedSeniorRows.length / pageSize));

        for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
          groupModels.push({
            viewType: VIEW_TYPE.GROUP_TABLE,
            groupWithAge: 'Overall Senior Results',
            events: visibleEvents,
            participantsData: [{ pageIndex, pageCount }],
          });
        }
      }
    }
  }

  return groupModels;
}
